/**
 * Painel de alarmes do CCO: prioridade, causa provável, ação recomendada e reconhecimento — Nexora Grid (US-403).
 *
 * Criado em: 16/10/2026 · Última modificação: 03/11/2026 · Versão: 1.2.0
 *
 * ISA-101: prioridade com forma + cor + texto; só o alarme NÃO reconhecido pisca. Alarmes repetidos chegam
 * agrupados (dispositivo + tipo, ISA-18.2). Reconhecer registra quem, quando e o comentário; uma ocorrência nova
 * reabre o alarme.
 *
 * Resolver (operador, supervisor, coordenador): encerra o alarme com um texto do que ficou resolvido; ele sai da lista e a
 * resolução fica registrada (`alarmes_resolucoes`).
 *
 * US-3108 (regra 9): "Apagar" (supervisor e coordenador, com motivo) só MARCA o alarme — ele sai desta lista e continua
 * no histórico completo, que quem tem permissão baixa em CSV (apagados e arquivados incluídos).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, baixar, ErroApi } from "../api";
import { fmtData } from "../cadastro/tipos";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { useCco, type GrupoAlarme } from "./contexto";
import { FORMA, NOME_PRIORIDADE, type Prioridade } from "./medidas";

type GrupoCompleto = GrupoAlarme & { causa_provavel?: string | null; confianca?: number | null; base_causa?: string;
  acao_recomendada?: string; comentario?: string };

export function Alarmes() {
  const { usuario } = useSessao();
  const { empresa, alarmes, recarregarAlarmes } = useCco();
  const [soAbertos, setSoAbertos] = useState(false);
  const [prioridade, setPrioridade] = useState<number | null>(null);
  const [comentarios, setComentarios] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const podeReconhecer = !!usuario && pode.manobrar(usuario);
  const lista = (alarmes as GrupoCompleto[]).filter((a) => (!soAbertos || !a.reconhecido) && (!prioridade || a.prioridade === prioridade));
  const reconhecer = async (a: GrupoCompleto) => {
    setErro(null);
    try {
      await api("/v1/alarmes/reconhecer", { corpo: { empresa, device_id: a.device_id, tipo: a.tipo,
        comentario: comentarios[`${a.device_id}|${a.tipo}`] ?? "" } });
      recarregarAlarmes();
    } catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); }
  };
  const [resolucoes, setResolucoes] = useState<Record<string, string>>({});
  const resolver = async (a: GrupoCompleto) => {
    setErro(null);
    try {
      await api("/v1/alarmes/resolver", { corpo: { empresa, device_id: a.device_id, tipo: a.tipo, texto: (resolucoes[`${a.device_id}|${a.tipo}`] ?? "").trim() } });
      setAviso("Alarme resolvido: saiu da lista e o texto ficou registrado no histórico.");
      recarregarAlarmes();
    } catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); }
  };
  const apagar = async (a: GrupoCompleto) => {
    setErro(null);
    try {
      const r = await api<{ apagados: number }>("/v1/historico/alarmes/apagar", { corpo: { empresa, device_id: a.device_id, tipo: a.tipo,
        motivo: motivos[`${a.device_id}|${a.tipo}`] ?? "" } });
      setAviso(`${r.apagados} ocorrência(s) apagada(s) da lista; continuam no histórico completo.`);
      recarregarAlarmes();
    } catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); }
  };
  const podeApagar = !!usuario && pode.apagarAlarme(usuario);
  return (
    <section className="painel-alarmes" aria-label="Alarmes">
      <div className="filtros">
        <label className="confirmar"><input type="checkbox" checked={soAbertos} onChange={(e) => setSoAbertos(e.target.checked)} /> só não reconhecidos</label>
        <div className="abas-situacao" role="group" aria-label="Prioridade">
          <button type="button" className={!prioridade ? "ativa" : ""} onClick={() => setPrioridade(null)}>todas</button>
          {([1, 2, 3, 4] as Prioridade[]).map((p) => (
            <button key={p} type="button" className={`${prioridade === p ? "ativa " : ""}prioridade-${p}`} onClick={() => setPrioridade(p)}>
              <span className="forma">{FORMA[p]}</span> {NOME_PRIORIDADE[p]}</button>
          ))}
        </div>
        {usuario && pode.verHistoricoCompleto(usuario) && empresa && (
          <button type="button" className="nx-botao" onClick={() => void baixar(`/v1/historico?empresa=${empresa}&tipo=alarmes&dias=90&formato=csv`,
            `historico_alarmes_${empresa}.csv`).catch((e) => setErro(e instanceof ErroApi ? e.message : String(e)))}>Histórico completo (CSV, 90 dias)</button>
        )}
      </div>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {aviso && <p className="nx-suave" role="status">{aviso}</p>}
      <ul className="lista-alarmes">
        {lista.map((a) => {
          const k = `${a.device_id}|${a.tipo}`;
          return (
            <li key={k} className={`nx-alarme item-alarme prioridade-${a.prioridade}`} data-prioridade={a.prioridade}
              data-reconhecido={a.reconhecido ? "sim" : "nao"}>
              <div className="corpo-alarme">
                <div className="linha-titulo-alarme">
                  <b>{a.tipo.replace(/_/g, " ").toLowerCase()}</b>
                  <span className="nx-selo">{NOME_PRIORIDADE[a.prioridade]}</span>
                  <Link to={`/cco/equipamento/${a.device_id}`}>{a.ativos[0] ?? a.device_id}</Link>
                  <span className="nx-suave">{a.unidade} · {a.ocorrencias}× · última {fmtData(a.ultimo)}</span>
                </div>
                {a.causa_provavel && (
                  <p className="causa">Causa provável: <b>{a.causa_provavel}</b>
                    <span className="nx-suave">{a.confianca != null ? ` · confiança ${(a.confianca * 100).toFixed(0)}%` : ""}{a.base_causa ? ` · ${a.base_causa}` : ""}</span></p>
                )}
                {a.acao_recomendada && <p className="acao">Ação recomendada: {a.acao_recomendada}</p>}
                {a.reconhecido && <p className="nx-suave">Reconhecido por {a.reconhecido_por} em {fmtData(a.reconhecido_em!)}{a.comentario ? ` — “${a.comentario}”` : ""}</p>}
              </div>
              {!a.reconhecido && podeReconhecer && (
                <div className="reconhecer">
                  <input aria-label={`comentário para ${a.tipo}`} placeholder="comentário (opcional)" value={comentarios[k] ?? ""}
                    onChange={(e) => setComentarios({ ...comentarios, [k]: e.target.value })} />
                  <button type="button" className="nx-botao nx-botao-principal" onClick={() => void reconhecer(a)}>Reconhecer</button>
                </div>
              )}
              {podeReconhecer && (
                <div className="reconhecer">
                  <input aria-label={`o que foi resolvido em ${a.tipo}`} placeholder="o que foi resolvido (obrigatório, fica registrado)" maxLength={500}
                    value={resolucoes[k] ?? ""} onChange={(e) => setResolucoes({ ...resolucoes, [k]: e.target.value })} />
                  <button type="button" className="nx-botao" disabled={(resolucoes[k] ?? "").trim().length < 10} onClick={() => void resolver(a)}>Resolver</button>
                </div>
              )}
              {podeApagar && (
                <div className="reconhecer">
                  <input aria-label={`motivo para apagar ${a.tipo}`} placeholder="motivo para apagar (fica no histórico)" maxLength={300}
                    value={motivos[k] ?? ""} onChange={(e) => setMotivos({ ...motivos, [k]: e.target.value })} />
                  <button type="button" className="nx-botao" disabled={(motivos[k] ?? "").trim().length < 5} onClick={() => void apagar(a)}>Apagar</button>
                </div>
              )}
            </li>
          );
        })}
        {!lista.length && <li className="nx-suave">nenhum alarme com esses filtros nas últimas 24 h</li>}
      </ul>
    </section>
  );
}
